/** Resolves browser and worker runtime settings and limits query-driven test behavior to supported environments. */
export type RuntimeConfigKey =
  | 'API_SERVER'
  | 'FILESERVER'
  | 'WEBSOCKETSERVER'
  | 'MDS_WS_SERVER'
  | 'OAS_WS_SERVER'
  | 'AUTHCLIENTID'
  | 'AUTHREDIRECT'
  | 'AUTHSTATE'
  | 'APP_ENV'
  | 'PREVIEW_MODE'
  | 'SUBSCRIPTION_PAYLOAD_LOG_MODE'
  | 'WORKSPACE_TAB_RENDER_ON_DEMAND';

export type RuntimeConfig = Partial<Record<RuntimeConfigKey, string>>;
export type RuntimeConfigInput = Record<string, unknown>;

const WORKER_QUERY_PREFIX = 'qepcfg_';
const RUNTIME_CONFIG_KEYS: RuntimeConfigKey[] = [
  'API_SERVER',
  'FILESERVER',
  'WEBSOCKETSERVER',
  'MDS_WS_SERVER',
  'OAS_WS_SERVER',
  'AUTHCLIENTID',
  'AUTHREDIRECT',
  'AUTHSTATE',
  'APP_ENV',
  'PREVIEW_MODE',
  'SUBSCRIPTION_PAYLOAD_LOG_MODE',
  'WORKSPACE_TAB_RENDER_ON_DEMAND',
];
const RUNTIME_CONFIG_ALIASES: Record<string, RuntimeConfigKey> = {
  apiServer: 'API_SERVER',
  appEnv: 'APP_ENV',
  fileServer: 'FILESERVER',
  previewMode: 'PREVIEW_MODE',
  workspaceTabRenderOnDemand: 'WORKSPACE_TAB_RENDER_ON_DEMAND',
  webSocketServer: 'WEBSOCKETSERVER',
  mdsWsServer: 'MDS_WS_SERVER',
  oasWsServer: 'OAS_WS_SERVER',
};
/** UAT is deployed as staging; production, preprod, and unknown environments remain excluded. */
export const TESTING_APP_ENVS = ['local', 'development', 'staging'] as const;
const TESTING_APP_ENV_SET = new Set<string>(TESTING_APP_ENVS);

type RuntimeConfigGlobal = typeof globalThis & {
  __QEP_RUNTIME_CONFIG__?: RuntimeConfig;
};

const readWindowConfig = (): RuntimeConfig => (globalThis as RuntimeConfigGlobal).__QEP_RUNTIME_CONFIG__ ?? {};

/**
 * Treats non-empty runtime values as usable so late Browser config does not erase earlier endpoint config.
 */
const isRuntimeConfigValuePresent = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

/**
 * Normalizes Browser camelCase payloads and worker/env uppercase payloads into one runtime-config shape.
 */
const normalizeRuntimeConfig = (config: RuntimeConfigInput): RuntimeConfig => {
  const normalized: RuntimeConfig = {};

  for (const key of RUNTIME_CONFIG_KEYS) {
    const value = config[key];
    if (isRuntimeConfigValuePresent(value)) {
      normalized[key] = String(value);
    }
  }

  for (const [sourceKey, runtimeConfigKey] of Object.entries(RUNTIME_CONFIG_ALIASES)) {
    const value = config[sourceKey];
    if (isRuntimeConfigValuePresent(value)) {
      normalized[runtimeConfigKey] = String(value);
    }
  }

  return normalized;
};

/**
 * Reads build environment values through direct property access so Parcel can inline preview/deploy endpoints.
 */
const readBuildTimeConfig = (): RuntimeConfig => normalizeRuntimeConfig({
  API_SERVER: process.env.API_SERVER,
  FILESERVER: process.env.FILESERVER,
  WEBSOCKETSERVER: process.env.WEBSOCKETSERVER,
  MDS_WS_SERVER: process.env.MDS_WS_SERVER,
  OAS_WS_SERVER: process.env.OAS_WS_SERVER,
  AUTHCLIENTID: process.env.AUTHCLIENTID,
  AUTHREDIRECT: process.env.AUTHREDIRECT,
  AUTHSTATE: process.env.AUTHSTATE,
  APP_ENV: process.env.APP_ENV,
  PREVIEW_MODE: process.env.PREVIEW_MODE,
  SUBSCRIPTION_PAYLOAD_LOG_MODE: process.env.SUBSCRIPTION_PAYLOAD_LOG_MODE,
  WORKSPACE_TAB_RENDER_ON_DEMAND: process.env.WORKSPACE_TAB_RENDER_ON_DEMAND,
});

/**
 * Stores runtime config received after script load so long-lived child windows can resolve environment endpoints.
 */
export const setRuntimeConfig = (config: RuntimeConfigInput): void => {
  const normalizedConfig = normalizeRuntimeConfig(config);
  (globalThis as RuntimeConfigGlobal).__QEP_RUNTIME_CONFIG__ = {
    ...readWindowConfig(),
    ...normalizedConfig,
  };
};

const readWorkerQueryConfig = (key: RuntimeConfigKey): string | undefined => {
  const {location} = globalThis;
  if (!location?.search) {
    return undefined;
  }

  return new URLSearchParams(location.search).get(`${WORKER_QUERY_PREFIX}${key}`) ?? undefined;
};

const readWorkerNameConfig = (key: RuntimeConfigKey): string | undefined => {
  const {name} = globalThis as typeof globalThis & {name?: string};
  if (!name?.includes(WORKER_QUERY_PREFIX)) {
    return undefined;
  }

  return new URLSearchParams(name.slice(name.indexOf(WORKER_QUERY_PREFIX))).get(`${WORKER_QUERY_PREFIX}${key}`) ?? undefined;
};

export const getRuntimeConfigValue = (key: RuntimeConfigKey): string | undefined => readWindowConfig()[key]
  ?? readWorkerQueryConfig(key)
  ?? readWorkerNameConfig(key)
  ?? readBuildTimeConfig()[key]
  ?? process.env[key];

export const getApiServer = (): string => getRuntimeConfigValue('API_SERVER') ?? '';
export const getFileServer = (): string => getRuntimeConfigValue('FILESERVER') ?? '';
export const getWebSocketServer = (): string => getRuntimeConfigValue('WEBSOCKETSERVER') ?? '';
export const getMdsWsServer = (): string => getRuntimeConfigValue('MDS_WS_SERVER') ?? '';
export const getOasWsServer = (): string => getRuntimeConfigValue('OAS_WS_SERVER') ?? '';
export const getAuthClientId = (): string => getRuntimeConfigValue('AUTHCLIENTID') ?? '';
export const getAuthRedirect = (): string => getRuntimeConfigValue('AUTHREDIRECT') ?? '';
export const getAuthState = (): string => getRuntimeConfigValue('AUTHSTATE') ?? '';
export const getAppEnv = (): string => getRuntimeConfigValue('APP_ENV') ?? '';
export const isPreviewMode = (): boolean => getRuntimeConfigValue('PREVIEW_MODE') === 'true';

/**
 * Resolves the subscription payload logging policy from runtime configuration.
 *
 * Compact summaries are intentionally the fallback so deployed workers do not retain complete
 * subscription payloads through DevTools unless a diagnostician explicitly requests `full`.
 */
export const getSubscriptionPayloadLogMode = (): 'summary' | 'full' | 'off' => {
  const mode = getRuntimeConfigValue('SUBSCRIPTION_PAYLOAD_LOG_MODE')?.trim().toLowerCase();
  if (mode === 'full' || mode === 'off') {
    return mode;
  }

  return 'summary';
};

/**
 * Controls whether FlexLayout defers inactive workspace-tab mounts.
 *
 * The default retains legacy eager initialization for real-time windows; deployments must explicitly
 * set `true` when the memory-saving deferred mount behavior is appropriate.
 */
export const isWorkspaceTabRenderOnDemand = (): boolean =>
  getRuntimeConfigValue('WORKSPACE_TAB_RENDER_ON_DEMAND')?.trim().toLowerCase() === 'true';

/**
 * Allows query-driven e2e behavior only for runtime environments that are safe for test fallbacks.
 */
export const isTestingAppEnv = (appEnv = getAppEnv()): boolean => TESTING_APP_ENV_SET.has(appEnv);

/**
 * Combines the runtime environment whitelist with the testing query flag used by e2e smoke tests.
 */
export const isTestingQueryEnabled = (search = globalThis.location?.search ?? ''): boolean =>
  isTestingAppEnv() && new URLSearchParams(search).get('testing') === 'true';

export const getWorkerConfigQueryPrefix = (): string => WORKER_QUERY_PREFIX;
